/**
 * TelcoTrack — BUDOVA workflow **variant context** (branch id 7 / 8 / 9).
 *
 * Responsibility: read `budovaWorkflowId` from metadata or infer from phase code prefix
 * (`BUDOVA.1/2/3.*`). Used for API query params and legacy Android/metadata paths.
 *
 * **Not** used for linear next/previous — the phase code already selects the branch.
 * Graph logic: `workflow-model.ts`. Milestone gates: `budova-milestone-engine.ts`.
 */

import type { WorkflowVariantContext } from './types';

export const WORKFLOW_METADATA_KEYS = {
  budovaWorkflowId: 'budovaWorkflowId',
} as const;

function readWorkflowId(
  metadata: Record<string, unknown> | null | undefined,
  customFields: Record<string, unknown>,
  ...keys: string[]
): number | undefined {
  for (const key of keys) {
    const raw = metadata?.[key] ?? customFields[key];
    if (raw == null || raw === '') continue;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isFinite(n)) return Math.trunc(n);
  }
  return undefined;
}

/** BUDOVA větev z kódu fáze (`BUDOVA.1/2/3.*` → 7/8/9). */
export function budovaBranchIdFromPhaseCode(phaseCode: string | null | undefined): 7 | 8 | 9 | undefined {
  const c = (phaseCode ?? '').trim();
  if (!c) return undefined;
  if (c.startsWith('BUDOVA.1.')) return 7;
  if (c.startsWith('BUDOVA.2.')) return 8;
  if (c.startsWith('BUDOVA.3.')) return 9;
  return undefined;
}

/**
 * BUDOVA varianta pro API query param a legacy metadata fallback.
 * Lineární navigace nepotřebuje variantu — větev je v `status`.
 */
export function parseWorkflowVariant(
  metadata: unknown,
  phaseCode?: string | null,
): WorkflowVariantContext {
  const m =
    metadata != null && typeof metadata === 'object' && !Array.isArray(metadata)
      ? (metadata as Record<string, unknown>)
      : null;
  const cf = (m?.['customFields'] as Record<string, unknown> | undefined) ?? {};

  let budovaWorkflowId = readWorkflowId(m, cf, WORKFLOW_METADATA_KEYS.budovaWorkflowId, 'pripojovanyWorkflowId') as
    | 7
    | 8
    | 9
    | undefined;

  const code = (phaseCode ?? '').trim();
  if (code) {
    budovaWorkflowId ??= budovaBranchIdFromPhaseCode(code);
  }

  const result: WorkflowVariantContext = {};
  if (budovaWorkflowId === 7 || budovaWorkflowId === 8 || budovaWorkflowId === 9) {
    result.budovaWorkflowId = budovaWorkflowId;
  }
  return result;
}
